import { Icon } from "./Icon";
import { describeCell, stripLetter, type WeekCell } from "../lib/weekLine";

/* THE WEEK LINE — the signature, spent once per row (Proposal 7 rule 5; the strip re-drawn by W106, William 2026-09-30).
 * The strip: every cell its day letter · done = the green fill · missed = the red tint, outline and letter · assigned =
 * a solid neutral outline · rest = no outline · today = a 2 px primary-ink bar UNDER the cell (red means missed, so
 * today is never red) — never the sweep: on a 24 px cell the diagonal reads as a broken corner (Fable, FIX A) ·
 * a task-only day = a quiet dot under the letter.
 * ⚠️ THERE IS NO "PART" (½) CELL, AND THAT IS AN ABSENT STATE, NOT AN UNBUILT DRAWING: the web's
 * derivation (`weekCells`) has no partial-day signal — a real ½ needs a per-day prescribed-vs-logged
 * read, filed for W72 step 2 (the client Week needs that read anyway). Drawing ½ from a guess would
 * tell a coach a client half-trained a day nobody measured. */

/* The week line, two sizes: `strip` (a roster column) and `cards` (the client masthead).
 * A mark as well as a colour, never colour alone: in the cards, done = tick, missed = open ring, due = dot; in the strip
 * the non-colour cue is the SHAPE of the fill — solid (done), tint + outline (missed), outline alone (due). The cells fill left→right once on mount (direction.css), the phone's
 * draw-once rule; reduced motion keeps the fade only. */
export function WeekLine({ cells, variant = "strip", label = "This week" }: {
  cells: WeekCell[];
  variant?: "strip" | "cards";
  label?: string;
}) {
  return (
    <ol className={`weekline weekline-${variant}`} aria-label={label}>
      {cells.map((c, i) => (
        <li
          key={c.dayKey}
          className={`weekline-cell is-${c.state}${c.isToday ? " is-today" : ""}`}
          style={{ ["--i" as string]: i }}
          title={describeCell(c)}
        >
          <span className="ht-visually-hidden">{describeCell(c)}</span>
          {variant === "cards" && (
            <span aria-hidden="true" className="weekline-day">
              <span className="weekline-dow">{c.label}</span>
              <span className="weekline-dom">{c.dayOfMonth}</span>
            </span>
          )}
          <span aria-hidden="true" className="weekline-mark">
            {/* The tick is the CARDS variant's mark; the strip says done by its green fill (W106). */}
            {variant === "cards" && c.state === "done" && (
              <Icon name="check" size={14} stroke={3} />
            )}
            {/* 🔴 W106 (William, 2026-09-30: *"just have M to S, and the miss is highlighted red, and done highlighted
                green"*) — EVERY strip cell carries its DAY LETTER, whatever it holds: done = the green fill, missed = the
                red tint and outline, an assigned day to come = a solid outline, a day with nothing = no outline (pro.css).
                A rest day holding only a task keeps its small dot UNDER the letter (a task is reported, never scored). */}
            {variant === "strip" && <span className="weekline-letter">{stripLetter(c)}</span>}
            {variant === "strip" && c.state === "rest" && c.taskTitles.length > 0 && (
              <span className="weekline-taskdot" />
            )}
          </span>
          {variant === "cards" && (
            <span aria-hidden="true" className="weekline-titles">
              {c.titles.slice(0, 3).map((t) => <span key={t} className="weekline-title">{t}</span>)}
              {c.titles.length > 3 && <span className="weekline-more">+{c.titles.length - 3}</span>}
              {/* 🔴 THE DAY'S TASKS, QUIETLY — AND WITHOUT THEM THIS CARD LIED THE OTHER WAY.
                  Taking tasks out of the STATE stopped a rest day wearing a red "missed" ring, and
                  then made the same day read "rest" with nothing on it — while his wrist series
                  sat there. A task is reported, never scored: shown in the secondary colour, and
                  only "rest" when the day is genuinely empty. */}
              {c.taskTitles.slice(0, 2).map((t) => <span key={t} className="weekline-task">{t}</span>)}
              {c.titles.length === 0 && c.taskTitles.length === 0 && <span className="weekline-rest">rest</span>}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}
